import React, { useState } from "react";
import { Crosshair } from "lucide-react";

/**
 * TrueLine Calibration brand mark.
 *
 * - Serves the local PNG from `/assets/trueline-logo.png` (downloaded once, no remote hotlink).
 * - Falls back to the Crosshair icon inside the brand gradient tile if the image fails to load.
 *
 * Props:
 *  - size: "sm" (32px) | "md" (40px, default) | "lg" (64px) — controls the wrapping tile/height
 *  - variant: "tile" (default — colored gradient square + image inside) | "bare" (no tile, just the image)
 *  - className: extra wrapper classes
 *  - alt: optional alt text override
 */
export default function BrandLogo({ size = "md", variant = "tile", className = "", alt = "TrueLine Calibration" }) {
  const [failed, setFailed] = useState(false);

  const sizeBox = {
    sm: { wrap: "w-8 h-8 rounded-md", img: "w-6 h-6", icon: "w-4 h-4" },
    md: { wrap: "w-10 h-10 rounded-lg", img: "w-7 h-7", icon: "w-5 h-5" },
    lg: { wrap: "w-16 h-16 rounded-2xl", img: "w-11 h-11", icon: "w-8 h-8" },
  }[size] || { wrap: "w-10 h-10 rounded-lg", img: "w-7 h-7", icon: "w-5 h-5" };

  if (variant === "bare") {
    if (failed) {
      return (
        <span className={`inline-flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-400 rounded-md ${sizeBox.wrap} ${className}`}>
          <Crosshair className={`${sizeBox.icon} text-white`} strokeWidth={2.2} />
        </span>
      );
    }
    return (
      <img
        src="/assets/trueline-logo.png"
        alt={alt}
        data-testid="brand-logo"
        onError={() => setFailed(true)}
        className={`object-contain ${sizeBox.wrap} ${className}`}
        style={{ filter: "drop-shadow(0 0 8px rgba(0,180,255,0.18))" }}
      />
    );
  }

  // tile variant — gradient square with logo inside (mimics the prior Crosshair tile)
  return (
    <div
      className={`flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-400 shadow-lg shadow-blue-500/20 ${sizeBox.wrap} ${className}`}
      data-testid="brand-logo-tile"
    >
      {failed ? (
        <Crosshair className={`${sizeBox.icon} text-white`} strokeWidth={2.2} />
      ) : (
        <img
          src="/assets/trueline-logo.png"
          alt={alt}
          data-testid="brand-logo"
          onError={() => setFailed(true)}
          className={`${sizeBox.img} object-contain`}
        />
      )}
    </div>
  );
}
